import React from 'react';
import { m } from 'framer-motion';
import { XIcon, CheckIcon } from 'lucide-react';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SectionTag } from '../ui/SectionTag';
import { ScrollHintTable } from '../ui/ScrollHintTable';
import { scrollToId } from '../utils/scroll';
const rows = [{
  n: '1/4',
  title: 'Planificación',
  before: 'Pierdes horas buscando ejercicios y organizando sesiones desde cero.',
  after: 'Partes de entrenamientos estructurados por edades y objetivos.'
}, {
  n: '2/4',
  title: 'Seguridad',
  before: 'No siempre sabes cómo adaptar las tareas al nivel de tus jugadores.',
  after: 'Cuentas con progresiones y variantes para ajustar el entrenamiento.'
}, {
  n: '3/4',
  title: 'Variedad',
  before: 'Terminas repitiendo los mismos ejercicios porque no tienes otras tareas preparadas.',
  after: 'Dispones de ejercicios y situaciones de juego para trabajar diferentes objetivos.'
}, {
  n: '4/4',
  title: 'Energía',
  before: 'Llegas mentalmente agotado después de preparar todo a última hora.',
  after: 'Dedicas más atención a observar, corregir y dirigir a tu equipo.'
}];
const footerStats = [{
  value: '+120h',
  label: 'Ahorradas al año'
}, {
  value: '2 clics',
  label: 'Para cada sesión lista'
}, {
  value: '100%',
  label: 'Profesional'
}];
export function Transformation() {
  return <section className="bg-bg-primary py-10 sm:py-14" data-id="element-231" data-mp="mh5g3ezdlztk">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6" data-id="element-232" data-mp="mjrothy06ldd">
        <SectionTag icon="⚡" data-id="element-233" data-mp="rhkt25fqx5b0">La transformación</SectionTag>
        <h2 className="mt-5 font-heading text-3xl font-bold uppercase leading-tight text-white sm:text-4xl" data-id="element-234" data-mp="dha10z2p9ko9">
          ¿Cuántas horas pierdes preparando lo que vas a trabajar en tu próximo entrenamiento?
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg" data-id="element-235" data-mp="27uhxev6bq4c0">
          Terminas el día y todavía tienes que buscar ejercicios, organizar las tareas y decidir cómo
          adaptarlas al nivel de tus jugadores.
        </p>
        <p className="mt-3 text-base leading-relaxed text-muted sm:text-lg" data-id="element-236" data-mp="3kv4nixvao5nz">
          Guardas ideas de diferentes sitios, repites ejercicios que ya conoces y acabas preparando
          sesiones sin una progresión clara.
        </p>

        <div className="mt-6 rounded-2xl border border-orange/20 bg-orange/5 p-5 text-left sm:p-6" data-id="element-237" data-mp="36tsbuyn62q7w">
          <p className="font-heading text-sm font-semibold uppercase tracking-wide text-orange" data-id="element-238" data-mp="7enic407ipfv">
            Una estructura, no más ejercicios sueltos
          </p>
          <p className="mt-2 text-sm leading-relaxed text-muted sm:text-base" data-id="element-239" data-mp="3dv9vcz5sokn">
            No necesitas seguir acumulando ejercicios. Necesitas una estructura para saber qué trabajar,
            cómo organizarlo y qué corregir.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-white sm:text-base" data-id="element-240" data-mp="r2e8t1wbjp02">
            Con <span className="font-semibold" data-id="element-241" data-mp="mbxttdcunvbl">El Manual del Entrenador de Baloncesto</span> puedes partir
            de sesiones organizadas y adaptarlas a las necesidades reales de tu equipo.
          </p>
        </div>
      </div>

      <div className="mx-auto mt-8 max-w-4xl px-4 sm:px-6" data-id="element-242" data-mp="yldp2yohh0f7">
        <div className="space-y-4" data-id="element-243" data-mp="yo07jtcwx0c7">
          {rows.map((row, i) => <m.div key={row.title} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }} className="rounded-2xl border border-white/10 bg-bg-secondary p-5 sm:p-6" data-id="element-244" data-mp="13jp19g293n0l" data-mpi={i}>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted" data-id="element-245" data-mp="1mbzz2vi1suru">
                {row.title} · {row.n}
              </p>
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-246" data-mp="1rab8x68cf5a2">
                <div className="flex items-start gap-3" data-id="element-247" data-mp="1vcy1j9cu8u8b">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-danger/15" data-id="element-248" data-mp="1vad0gmkd6ibm">
                    <XIcon size={14} className="text-danger" data-id="element-249" data-mp="1mok2i0vzd31k" />
                  </div>
                  <div data-id="element-250" data-mp="1mmfsr2beh9td">
                    <p className="text-xs font-semibold uppercase tracking-wide text-danger" data-id="element-251" data-mp="1inqhbf9xa2cd">Antes</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted" data-id="element-252" data-mp="1wf51vqjhablj">{row.before}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-253" data-mp="1rg232nu3d1m0">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-success/15" data-id="element-254" data-mp="13peh4ynccbgg">
                    <CheckIcon size={14} className="text-success" data-id="element-255" data-mp="zpnwlcoeqrkd" />
                  </div>
                  <div data-id="element-256" data-mp="1dp5d74rjuyin">
                    <p className="text-xs font-semibold uppercase tracking-wide text-success" data-id="element-257" data-mp="1dpotyi5grgv3">Ahora</p>
                    <p className="mt-1 text-sm leading-relaxed text-white" data-id="element-258" data-mp="18nprdahjsnz7">{row.after}</p>
                  </div>
                </div>
              </div>
            </m.div>)}
        </div>

        <div className="mt-8 flex justify-center" data-id="element-259" data-mp="2k3xy7o40ilgo">
          <PrimaryButton onClick={() => scrollToId('oferta-base')} data-id="element-260" data-mp="5vx115b6mpig">Quiero dejar de improvisar</PrimaryButton>
        </div>

        <div className="mt-8" data-id="element-261" data-mp="awkuryqspkgd">
          <ScrollHintTable minWidth="480px" data-id="element-262" data-mp="atf1e7b4vweu">
            <div className="grid grid-cols-3 divide-x divide-white/10" data-id="element-263" data-mp="3t1o3rujvugm2">
              {footerStats.map(stat => <div key={stat.label} className="flex flex-col items-center gap-1 px-3 py-6 text-center" data-id="element-264" data-mp="1vuxsrpsto12">
                  <span className="font-heading text-xl font-bold text-orange sm:text-2xl" data-id="element-265" data-mp="plp8p3qk52io">{stat.value}</span>
                  <span className="text-xs text-muted sm:text-sm" data-id="element-266" data-mp="tlhp22m70cyl">{stat.label}</span>
                </div>)}
            </div>
          </ScrollHintTable>
        </div>
      </div>
    </section>;
}